import { useFormFallbackPrices } from '../useFormPrices/useFormFallbackPrices';
import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { IPriceRule } from '../../types/price.types';
import { DELIMITER, NULL } from '../../helpers/priceTable/constants';
import {
  applyPriceRules,
  type CalculationStep,
  resolveEffectiveRules,
} from '@weareopenr/common/build/src/utilities/pricing';
import { IPricingCategory } from '../../api/getPricingCategories/types';

export interface IGetPlaceholderReturn {
  calculationSteps: CalculationStep[];
  inheritedPlaceholder: string | null;
  calculatedPlaceholder: string | null;
  inputValue: number | null;
}

export const DEFAULT_PLACEHOLDER = null;

const hasValue = (value: number | null | undefined): value is number => {
  return value !== null && value !== undefined;
};

const formatRule = (rule: IPriceRule) => ({
  ...rule,
  extension_instance: rule.extension_instance?.id ? { id: rule.extension_instance.id } : null,
  price_band: rule.price_band?.id ? { id: rule.price_band.id } : null,
});

// Rules store their category in either pricing_category.id or pricing_category_id — check both,
// mirroring the pattern used in findInheritedRule.
const getRuleCategoryId = (rule: IPriceRule) => rule.pricing_category?.id ?? rule.pricing_category_id ?? null;

/***
 * Returns a inherited or calculated placeholder for a price input field based on the field, current form state and applicable price rules.
 * If the input field has a value or is a base price, it returns a default placeholder.
 * If there are applicable price rules, it calculates the placeholder based on those rules.
 * If no applicable rules are found, it inherits the placeholder from another field.
 * @param inputName - The name of the input field in the format "itemId*extensionInstanceId*priceBandId".
 * @param control - The control object from react-hook-form to access form state.
 * @param priceRules - An array of price rules that may apply to the input field.
 * @return An object containing the calculated placeholder and inherited placeholder.
 */
export const usePlaceholder = (
  inputName: string,
  control: Control,
  priceRules: IPriceRule[] | undefined,
  categoryId?: string | null,
  pricingCategories?: IPricingCategory[],
): IGetPlaceholderReturn => {
  const [itemId, extensionInstanceId, priceBandId] = inputName.split(DELIMITER);

  const {
    basePrice,
    priceBandDefault,
    extensionInstanceDefault,
    inputValue: { value_with_tax: inputValue },
  } = useFormFallbackPrices(itemId, extensionInstanceId, priceBandId, control);

  // Separate base rules from category-specific rules, then resolve the effective
  // rule set for this item's category. This de-duplicates rules per extension_instance_id
  // and price_band_id, which applyPriceRules requires.
  const effectiveRules = useMemo(() => {
    const globalRules = (priceRules ?? []).filter((rule) => !getRuleCategoryId(rule)).map(formatRule);

    if (pricingCategories && categoryId !== undefined) {
      const resolvedCategories = pricingCategories
        .filter((cat) => cat.category_id !== null)
        .map((cat) => ({
          id: cat.category_id,
          parent_id: cat.parent_category_id,
          rules: (priceRules ?? []).filter((r) => getRuleCategoryId(r) === cat.category_id).map(formatRule),
        }));
      return resolveEffectiveRules(categoryId, resolvedCategories, globalRules);
    }

    // No category context — use global rules only (never have duplicate IDs).
    return globalRules;
  }, [priceRules, pricingCategories, categoryId]);

  // if there is a value in the input field or this field is a base price;
  // there is no need to calculate a placeholder or inherit it
  if (hasValue(inputValue) || (extensionInstanceId === NULL && priceBandId === NULL)) {
    return { calculationSteps: [], calculatedPlaceholder: null, inheritedPlaceholder: DEFAULT_PLACEHOLDER, inputValue };
  }

  const applicableRules = effectiveRules.filter(
    (rule) => rule.price_band?.id === priceBandId || rule.extension_instance?.id === extensionInstanceId,
  );

  if (applicableRules.length > 0) {
    const prices = [basePrice];

    if (hasValue(priceBandDefault?.value_with_tax)) {
      prices.push(priceBandDefault);
    }
    if (hasValue(extensionInstanceDefault?.value_with_tax)) {
      prices.push(extensionInstanceDefault);
    }
    const target = { extension_instance_id: extensionInstanceId, price_band_id: priceBandId };
    const calculatedPrices = applyPriceRules(prices, applicableRules, target);

    // Matched directly rather than via common's selectHierarchicalPrice: passing a target makes
    // applyPriceRules return only that combination, and the ids here are still the form's NULL
    // string sentinels, which selectHierarchicalPrice's `?? null` normalisation does not collapse.
    const currentPrice = calculatedPrices.find(
      (price) => price.extension_instance_id === extensionInstanceId && price.price_band_id === priceBandId,
    );
    const calculationSteps = currentPrice?.calculationSteps;
    const calculatedPlaceholder = currentPrice?.value_with_tax;
    if (hasValue(calculatedPlaceholder)) {
      return {
        calculationSteps,
        calculatedPlaceholder: calculatedPlaceholder.toFixed(2),
        inheritedPlaceholder: null,
        inputValue,
      };
    }
  }

  // if there are no rules affecting the cell, inherit the placeholder from another field
  const fallbackPrices = [priceBandDefault, extensionInstanceDefault, basePrice].map((price) => price?.value_with_tax);
  const inheritedPlaceholder = fallbackPrices.find(hasValue);

  return {
    calculationSteps: [],
    calculatedPlaceholder: null,
    inheritedPlaceholder: hasValue(inheritedPlaceholder) ? inheritedPlaceholder.toFixed(2) : DEFAULT_PLACEHOLDER,
    inputValue,
  };
};
